'use client'

import { useRef } from 'react'
import Image from 'next/image'
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from 'motion/react'

const MotionImage = motion(Image)

export function GrayscaleTransitionImage({ width, height, fill, className, src, style: _style, ...props }) {
  let ref = useRef(null)
  let { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 65%', 'end 35%'],
  })
  let grayscale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0, 1])
  let filter = useMotionTemplate`grayscale(${grayscale})`

  // Static imports resolve to a StaticImageData object whose own width/height
  // Next.js Image will use natively — treat them as having dimensions so we
  // don't fall back to fill mode on a parent that has no fixed height.
  const isStaticImageData =
    typeof src === 'object' && src !== null && 'src' in src
  const hasDimensions = !!(width || height) || isStaticImageData
  const useFill = fill || !hasDimensions

  const imageProps = useFill
    ? { fill: true, className: `object-cover ${className ?? ''}` }
    : {
        ...(width ? { width } : null),
        ...(height ? { height } : null),
        className,
      }

  // fill mode requires the parent to be position:relative with a size.
  const wrapperClass = useFill
    ? 'group relative w-full overflow-hidden'
    : 'group relative'

  return (
    <div ref={ref} className={wrapperClass}>
      <MotionImage alt="" style={{ filter }} src={src} {...props} {...imageProps} />
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-0 transition duration-300 group-hover:opacity-100"
        aria-hidden="true"
      >
        <Image alt="" src={src} {...props} {...imageProps} />
      </div>
    </div>
  )
}
